import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { fetchAccuracy } from "../lib/api";
import { ChartLineUp, ArrowLeft, Target, Trophy, ArrowsClockwise, CheckCircle, XCircle, GridFour, ShareNetwork, TrendUp } from "@phosphor-icons/react";
import { formatPct } from "../lib/format";
import CalibrationPlot from "../components/CalibrationPlot";
import TrendChart from "../components/TrendChart";
import { fetchAccuracyTrend } from "../lib/api";
import { toast } from "sonner";

function MetricCard({ label, value, sub, accent }) {
  return (
    <div className="border border-white/10 p-5 bg-zinc-950/40">
      <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mb-2">
        {label}
      </div>
      <div className={`font-mono text-3xl tabular-nums font-medium ${accent || ""}`}>
        {value}
      </div>
      {sub && <div className="mt-1 text-xs font-body text-zinc-500">{sub}</div>}
    </div>
  );
}

export default function AIAccuracy() {
  const [data, setData] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (refresh = false) => {
    if (refresh) setRefreshing(true);
    try {
      const [d, t] = await Promise.all([fetchAccuracy(refresh), fetchAccuracyTrend()]);
      setData(d);
      setTrend(t.items || []);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(false); }, []);

  const shareToX = () => {
    if (!data || data.resolvedCount === 0) {
      toast.error("Track record will be shareable once some markets resolve.");
      return;
    }
    const ai = data.aiBrier?.toFixed(3);
    const mkt = data.marketBrier?.toFixed(3);
    const beats = data.aiBeatsMarket;
    const text =
      `MFSMAI Oracle's AI is ${beats ? "beating" : "trailing"} Polymarket on Brier score: ` +
      `AI ${ai} vs Market ${mkt} across ${data.resolvedCount} resolved markets.\n\n` +
      `Live track record →`;
    const url = `${window.location.origin}/accuracy`;
    const intent = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
    window.open(intent, "_blank", "noopener,noreferrer");
  };

  const copyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/accuracy`);
      toast.success("Track record URL copied");
    } catch {
      toast.error("Could not copy URL");
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="accuracy-page">
      <Header />
      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <Link
          to="/"
          className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft size={14} /> Back
        </Link>

        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <ChartLineUp size={22} weight="duotone" className="text-edge" />
            <h1 className="font-display text-3xl font-medium">MFSMAI Oracle Track Record</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="accuracy-share-btn"
              onClick={shareToX}
              className="text-xs font-mono uppercase tracking-widest text-black bg-edge hover:bg-white border border-edge hover:border-white px-3 py-1.5 inline-flex items-center gap-1.5 transition-colors"
            >
              <ShareNetwork size={12} weight="fill" />
              Share on X
            </button>
            <button
              data-testid="accuracy-copy-link-btn"
              onClick={copyShareLink}
              title="Copy link"
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 transition-colors"
            >
              Copy
            </button>
            <button
              data-testid="accuracy-refresh-btn"
              disabled={refreshing}
              onClick={() => load(true)}
              className="text-xs font-mono uppercase tracking-widest text-zinc-400 hover:text-white border border-white/15 hover:border-white/40 px-3 py-1.5 inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              <ArrowsClockwise size={12} className={refreshing ? "animate-spin" : ""} />
              {refreshing ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>
        <p className="text-sm text-zinc-400 font-body max-w-2xl mb-8">
          Every AI forecast is logged. When the underlying Polymarket market resolves, we compare
          MFSMAI Oracle's probability against the actual outcome — and against what the market was
          pricing. Lower Brier score is better.
        </p>

        {loading ? (
          <div className="text-zinc-500 font-mono text-xs uppercase tracking-widest">
            Loading…
          </div>
        ) : !data || data.resolvedCount === 0 ? (
          <div className="border border-white/10 p-10 text-center font-body text-zinc-400">
            <Target size={28} weight="duotone" className="mx-auto mb-3 text-edge" />
            <div className="text-base">No resolved forecasts yet.</div>
            <div className="text-sm text-zinc-500 mt-2">
              Track record will populate as forecasted Polymarket markets resolve.
              {data ? ` (${data.totalForecasts} forecasts pending resolution)` : ""}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <MetricCard
                label="Resolved"
                value={data.resolvedCount}
                sub={`of ${data.totalForecasts} total`}
                accent="text-zinc-100"
              />
              <MetricCard
                label="AI Brier Score"
                value={data.aiBrier?.toFixed(3)}
                sub="Lower = better"
                accent={data.aiBeatsMarket ? "text-profit" : "text-zinc-100"}
              />
              <MetricCard
                label="Market Brier"
                value={data.marketBrier?.toFixed(3)}
                sub="Crowd baseline"
                accent="text-zinc-100"
              />
              <MetricCard
                label="AI Accuracy"
                value={formatPct(data.aiAccuracy, 0)}
                sub={`vs market ${formatPct(data.marketAccuracy, 0)}`}
                accent={data.aiAccuracy > data.marketAccuracy ? "text-profit" : "text-zinc-100"}
              />
            </div>

            {data.aiBeatsMarket != null && (
              <div
                className={`border ${
                  data.aiBeatsMarket ? "border-profit/40" : "border-loss/40"
                } p-4 mb-8 flex items-center gap-3`}
              >
                <Trophy size={18} weight="duotone" className={data.aiBeatsMarket ? "text-profit" : "text-loss"} />
                <span className="font-display text-sm">
                  {data.aiBeatsMarket
                    ? "MFSMAI Oracle is currently outperforming the Polymarket crowd on Brier score."
                    : "Polymarket crowd is currently outperforming MFSMAI Oracle on Brier score."}
                </span>
              </div>
            )}

            {data.calibration && data.calibration.some((b) => b.count > 0) && (
              <div className="border border-white/10 p-6 mb-8 bg-zinc-950/40" data-testid="calibration-card">
                <div className="flex items-center gap-2 mb-1">
                  <GridFour size={16} weight="duotone" className="text-edge" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-edge">
                    ◢ Calibration plot
                  </div>
                </div>
                <h3 className="font-display text-lg font-medium mb-1">
                  Predicted probability vs actual outcome frequency
                </h3>
                <p className="text-sm text-zinc-400 font-body mb-5">
                  A well-calibrated forecaster's points hug the dashed diagonal. Above the line
                  means the AI was too pessimistic in that bucket; below means too optimistic.
                </p>
                <CalibrationPlot calibration={data.calibration} />
              </div>
            )}

            {trend.length > 0 && (
              <div className="border border-white/10 p-6 mb-8 bg-zinc-950/40" data-testid="trend-card">
                <div className="flex items-center gap-2 mb-1">
                  <TrendUp size={16} weight="duotone" className="text-edge" />
                  <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-edge">
                    ◢ Brier trend (weekly)
                  </div>
                </div>
                <h3 className="font-display text-lg font-medium mb-1">
                  AI vs market Brier score over time
                </h3>
                <p className="text-sm text-zinc-400 font-body mb-5">
                  Each point averages all forecasts whose markets resolved that ISO week.
                  Lower is better — when MFSMAI Oracle's line stays below the dashed market line,
                  the AI is calling outcomes more accurately than the crowd.
                </p>
                <TrendChart items={trend} />
              </div>
            )}

            <div className="border border-white/10">
              <div className="grid grid-cols-12 text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 px-4 py-3 border-b border-white/10 bg-white/[0.02]">
                <div className="col-span-6">Market</div>
                <div className="col-span-1 text-right">AI</div>
                <div className="col-span-1 text-right">MKT</div>
                <div className="col-span-1 text-center">Actual</div>
                <div className="col-span-1 text-right">AI Brier</div>
                <div className="col-span-2 text-right">Resolved</div>
              </div>
              <div className="divide-y divide-white/5">
                {data.resolved.map((r) => (
                  <Link
                    key={r.marketId}
                    to={`/market/${r.marketId}`}
                    data-testid={`accuracy-row-${r.marketId}`}
                    className="grid grid-cols-12 items-center px-4 py-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="col-span-6 flex items-center gap-3 min-w-0">
                      {r.image && <img src={r.image} alt="" className="w-7 h-7 object-cover border border-white/10 shrink-0" />}
                      <div className="font-body text-sm line-clamp-1">{r.question}</div>
                    </div>
                    <div className={`col-span-1 text-right font-mono tabular-nums text-sm ${r.aiCorrect ? "text-profit" : "text-loss"}`}>
                      {formatPct(r.aiProbability, 0)}
                    </div>
                    <div className="col-span-1 text-right font-mono tabular-nums text-sm text-zinc-400">
                      {formatPct(r.marketYesPrice, 0)}
                    </div>
                    <div className="col-span-1 text-center font-mono text-xs">
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 ${
                          r.actual === 1 ? "bg-yes/10 text-yes" : "bg-no/10 text-no"
                        }`}
                      >
                        {r.actualLabel}
                      </span>
                    </div>
                    <div className="col-span-1 text-right font-mono tabular-nums text-xs text-zinc-300 inline-flex items-center justify-end gap-1">
                      {r.aiCorrect ? (
                        <CheckCircle size={12} weight="fill" className="text-profit" />
                      ) : (
                        <XCircle size={12} weight="fill" className="text-loss" />
                      )}
                      {r.aiBrier.toFixed(3)}
                    </div>
                    <div className="col-span-2 text-right font-mono tabular-nums text-[11px] text-zinc-500">
                      {r.endDate ? new Date(r.endDate).toLocaleDateString() : "—"}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
